/** Zapytania modułu `tasks` (TanStack Query v5). */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Query } from '@/lib/api'
import { api } from '@/lib/api'
import type { Paginated } from '@/lib/types'
import type {
  CaseOption,
  LawyerOption,
  Task,
  TaskCreatePayload,
  TaskListResponse,
  TaskUpdatePayload,
} from './types'

export const tasksKeys = {
  all: ['tasks'] as const,
  list: (params: Query) => ['tasks', 'list', params] as const,
  lawyers: ['tasks', 'lawyers'] as const,
  cases: ['tasks', 'cases'] as const,
}

export interface TaskFilters {
  page?: number
  size?: number
  case_id?: number
  assignee_id?: number
  status?: string
  /** tylko zadania przypisane do mnie (przełącznik „Moje") */
  mine?: boolean
  /** tylko statusy open/in_progress (checkbox „tylko otwarte") */
  open_only?: boolean
}

/** Lista zadań (paginacja serwerowa; sort backendu: termin rosnąco, NULL na końcu). */
export function useTasks(filters: TaskFilters = {}) {
  const query: Query = { size: 50, ...filters }
  return useQuery({
    queryKey: tasksKeys.list(query),
    queryFn: () => api.get<TaskListResponse>('/tasks', { query }),
    placeholderData: (previous) => previous,
  })
}

/** Prawnicy do pola „Przypisany" — endpoint modułu `cases` (zawsze włączony),
 *  ten sam wzorzec, którym DeadlinesPage wybiera właściciela terminu. */
export function useLawyers(enabled = true) {
  return useQuery({
    queryKey: tasksKeys.lawyers,
    queryFn: () => api.get<LawyerOption[]>('/cases/lawyers'),
    staleTime: 5 * 60_000,
    enabled,
  })
}

/** Sprawy do wyboru w formularzu nowego zadania — jak w module `timesheet`. */
export function useCaseOptions(enabled = true) {
  return useQuery({
    queryKey: tasksKeys.cases,
    queryFn: () => api.get<Paginated<CaseOption>>('/cases', { query: { size: 200 } }),
    staleTime: 5 * 60_000,
    enabled,
  })
}

export function useCreateTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: TaskCreatePayload) => api.post<Task>('/tasks', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tasksKeys.all })
    },
  })
}

export function useUpdateTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...payload }: TaskUpdatePayload & { id: number }) =>
      api.patch<Task>(`/tasks/${id}`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tasksKeys.all })
    },
  })
}

export function useCompleteTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<Task>(`/tasks/${id}/complete`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tasksKeys.all })
    },
  })
}
